<template>
  <v-container fluid>
    <!-- Campo Applicazione -->
    <v-row>
      <v-col cols="12">
        <v-textarea v-model="campoDatiApplicazione" hide-details filled label="Campo Applicazione" auto-grow rows="4"
          row-height="60" />
      </v-col>
    </v-row>

    <!-- Terzisti - Rischio - Tipologia - Date -->
    <v-row class="mt-2">
      <v-col cols="6" sm="3">
        <div class="text-subtitle-1 mb-1">Terzisti</div>
        <v-checkbox :disabled="uiState.terzisti === false" :input-value="uiState.terzisti === true"
          @change="aggiornaTerzisti(true)" label="SI" color="primary" hide-details />
        <v-checkbox :disabled="uiState.terzisti === true" :input-value="uiState.terzisti === false"
          @change="aggiornaTerzisti(false)" label="NO" color="primary" hide-details />
      </v-col>

      <v-col cols="6" sm="3">
        <div class="text-subtitle-1 mb-1">Rischio</div>
        <v-checkbox :disabled="uiState.rischio === false" :input-value="uiState.rischio === true"
          @change="aggiornaRischio(true)" label="Alto" color="primary" hide-details />
        <v-checkbox :disabled="uiState.rischio === true" :input-value="uiState.rischio === false"
          @change="aggiornaRischio(false)" label="Basso" color="primary" hide-details />
      </v-col>

      <v-col cols="6" sm="3">
        <div class="text-subtitle-1 mb-1">Tipologia</div>
        <v-checkbox v-model="uiState.tipologia.verniciatore" label="Verniciatore" color="primary" hide-details
          @change="notificaUpdate" />
        <v-checkbox v-model="uiState.tipologia.commercialista" label="Commercialista" color="primary" hide-details
          @change="notificaUpdate" />
        <v-checkbox v-model="uiState.tipologia.lamellatore" label="Lamellatore" color="primary" hide-details
          @change="notificaUpdate" />
      </v-col>

      <v-col cols="6" sm="3">
        <campo-data v-model="campoDatiDataIngresso" label="Data Ingresso" class-custom="mb-4" />
        <campo-data v-model="campoDatiDataRimozione" label="Data Rimozione" />
      </v-col>
    </v-row>

    <!-- Opzioni specifiche (Legno controllato, etc.) -->
    <v-row>
      <v-col cols="6" sm="3">
        <v-radio-group v-model="campoDatiLegnoControllato" hide-details>
          <template v-slot:label>
            <div>Legno controllato</div>
          </template>
          <v-radio label="SI" :value="true" />
          <v-radio label="NO" :value="false" />
        </v-radio-group>
      </v-col>
      <v-col cols="6" sm="3">
        <v-radio-group v-model="campoDatiMaterialeRiciclato" hide-details>
          <template v-slot:label>
            <div>Materiale riciclato</div>
          </template>
          <v-radio label="SI" :value="true" />
          <v-radio label="NO" :value="false" />
        </v-radio-group>
      </v-col>
      <v-col cols="6" sm="3">
        <v-radio-group v-model="campoDatiLogoSuProdotto" hide-details>
          <template v-slot:label>
            <div>Uso del logo sul prodotto</div>
          </template>
          <v-radio label="SI" :value="true" />
          <v-radio label="NO" :value="false" />
        </v-radio-group>
      </v-col>
      <v-col cols="6" sm="3">
        <v-radio-group v-model="campoDatiLogoPromozionale" hide-details>
          <template v-slot:label>
            <div>Uso del logo promozionale</div>
          </template>
          <v-radio label="SI" :value="true" />
          <v-radio label="NO" :value="false" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Codice licenza uso logo (solo PEFC di gruppo) -->
    <v-row v-if="tipoCertificazione === 'PEFC'">
      <v-col cols="12" sm="6" md="4">
        <v-text-field v-model="cliente.codiceLicenzaLogoPEFC" label="Codice licenza uso logo"
          placeholder="PEFC/18-32-220/XX" dense hide-details clearable />
      </v-col>
    </v-row>

    <!-- Approvazione uso logo: visibile solo se almeno una delle due spunte sopra e' attiva -->
    <v-row v-if="campoDatiLogoSuProdotto || campoDatiLogoPromozionale">
      <v-col cols="12">
        <approvazione-loghi :company-code="cliente.companyCode" :tipo-certificazione="tipoCertificazione" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import CampoData from "./CampoData.vue";
import ApprovazioneLoghi from "./ApprovazioneLoghi.vue";

// Genera un computed get/set che legge/scrive il campo FSC o PEFC del cliente a seconda di
// this.tipoCertificazione, per non ripetere lo stesso if/else per ciascuna coppia di campi.
function mappaCampo(campoFSC, campoPEFC) {
  return {
    get() {
      return this.tipoCertificazione === "FSC" ? this.cliente[campoFSC] : this.cliente[campoPEFC];
    },
    set(value) {
      this.cliente[this.tipoCertificazione === "FSC" ? campoFSC : campoPEFC] = value;
    },
  };
}

export default {
  name: "DatiGruppo",

  components: {
    CampoData,
    ApprovazioneLoghi,
  },

  props: {
    cliente: {
      type: Object,
      required: true,
    },
    uiState: {
      type: Object,
      required: true,
    },
    tipoCertificazione: {
      type: String,
      required: true,
      validator: (value) => ["FSC", "PEFC"].includes(value),
    },
  },

  computed: {
    // Ognuno di questi campi esiste in due varianti sul cliente (FSC/PEFC): mappaCampo() genera il
    // get/set che sceglie la variante giusta in base a tipoCertificazione, invece di ripetere lo
    // stesso if/else identico 6 volte.
    campoDatiApplicazione: mappaCampo("applicazione", "applicazionePEFC"),
    campoDatiDataIngresso: mappaCampo("dataIngresso", "dataIngressoPEFC"),
    campoDatiDataRimozione: mappaCampo("dataRimozione", "dataRimozionePEFC"),
    campoDatiLegnoControllato: mappaCampo("legnoControllato", "legnoControllatoPEFC"),
    campoDatiMaterialeRiciclato: mappaCampo("materialeRiciclato", "materialeRiciclatoPEFC"),
    campoDatiLogoSuProdotto: mappaCampo("logoSuProdotto", "logoSuProdottoPEFC"),
    campoDatiLogoPromozionale: mappaCampo("logoPromozionale", "logoPromozionalePEFC"),
  },

  methods: {
    aggiornaTerzisti(valore) {
      const nuovoState = { ...this.uiState, terzisti: valore };
      this.$emit("update:ui-state", nuovoState);
    },

    aggiornaRischio(valore) {
      const nuovoState = { ...this.uiState, rischio: valore };
      this.$emit("update:ui-state", nuovoState);
    },

    notificaUpdate() {
      this.$emit("update:ui-state", { ...this.uiState });
    },
  },
};
</script>
